import React, { useEffect, useRef, useState } from 'react';
import Switch from 'react-switch';

import { Api } from '@apis/client';
import Http from '@helpers/Http';
import Filter from '@helpers/Filter';

import './index.module.less';

import NavBar from '@components/NavBar';
import Toast from '@widgets/Toast';
import DateTimePicker from '@components/picker/EasyTimePicker';

const DevicePowerPlan = () => {
  const params = useRef(Filter.urlParams(window.location.search));
  const [forbidSwitchStatus, setForbidSwitchSwitchStatus] = useState(false);
  const [powerSwitchStatus, setPowerSwitchStatus] = useState(false);
  const [time, setTime] = useState({
    onTime: '08:00',
    offTime: '22:00',
  });

  const [showTimePicker, displayTimePicker] = useState({
    onTime: false,
    offTime: false,
  });

  const getInfo = async () => {
    const { data } = await Http.post(Api.GetDevicePowerPlanConfig, {
      snCode: params.current.sncode,
      company: 'lingge',
    });

    setForbidSwitchSwitchStatus(data.isForbidTurnOff);
    setPowerSwitchStatus(data.allowAutoOnOff);
    setTime({
      offTime: data.offTime,
      onTime: data.onTime,
    });
  };

  const changePowerConfig = async (checked: boolean) => {
    await saveChangeConfig({ allowAutoOnOff: checked });
    setPowerSwitchStatus(checked);
  };

  const saveChangeConfig = async (extra = {}) => {
    const condition = {
      isForbidTurnOff: forbidSwitchStatus,
      allowAutoOnOff: powerSwitchStatus,
      ...time,
      ...extra,
    };
    await Http.post(Api.PostDevicePowerPlanConfig, {
      snCode: params.current.sncode,
      company: 'lingge',
      ...condition,
    });
    Toast.show({
      title: '设置成功',
      type: 'strip',
    });
  };

  const changeForbidConfig = async (checked: boolean) => {
    await saveChangeConfig({ isForbidTurnOff: checked });
    setForbidSwitchSwitchStatus(checked);
  };

  const translateTimestamp = (time: string) => {
    let s = 0;
    if (time !== null) {
      const hour = time.split(':')[0];
      const min = time.split(':')[1];
      s = Number(hour) * 3600 + Number(min) * 60;
    }
    return s;
  };

  const confirmTime = async (value: string, type: string) => {
    if (type === 'onTime') {
      const date1 = translateTimestamp(value);
      const date2 = translateTimestamp(time.offTime);
      if (date1 >= date2) {
        return Toast.show({
          title: '开始时间不得晚于结束时间',
          type: 'strip',
        });
      }
    }

    if (type === 'offTime') {
      const date1 = translateTimestamp(time.onTime);
      const date2 = translateTimestamp(value);
      if (date1 >= date2) {
        return Toast.show({
          title: '结束时间不得早于开始时间',
          type: 'strip',
        });
      }

      if (date2 - 10 * 60 < date1) {
        return Toast.show({
          title: '开关机时间间隔不得小于10分钟',
          type: 'strip',
        });
      }
    }

    await saveChangeConfig({ [type]: value });
    setTime({
      ...time,
      [type]: value,
    });
  };

  const controlTimePicker = (data = {}) => {
    displayTimePicker({
      ...showTimePicker,
      ...data,
    });
  };

  useEffect(() => {
    getInfo();
  }, []);

  return (
    <div className="flex container" styleName="container">
      <NavBar title="开关机管理" />
      <div styleName="content">
        <div className="flex" styleName="card">
          <div className="flex" styleName="card-info">
            <div styleName="card-title">限制关机</div>
            <Switch
              styleName="border-switch"
              uncheckedIcon={false}
              checkedIcon={false}
              offColor={'#ffffff'}
              onColor={'#256eff'}
              boxShadow={'0 0 1px 1px rgba(0, 0, 0, 0.2)'}
              activeBoxShadow={'0 0 1px 1px rgba(0, 0, 0, 0.08)'}
              width={51}
              height={28}
              onChange={checked => changeForbidConfig(checked)}
              checked={forbidSwitchStatus}
            />
          </div>
          <div styleName="card-desc">开启后，设备不能自由关机。</div>
        </div>

        <div
          className="flex"
          styleName="card"
          style={powerSwitchStatus ? { paddingBottom: 0 } : {}}
        >
          <div className="flex" styleName="card-info">
            <div styleName="card-title">定时开关机</div>
            <Switch
              styleName="border-switch"
              uncheckedIcon={false}
              checkedIcon={false}
              offColor={'#ffffff'}
              onColor={'#256eff'}
              boxShadow={'0 0 1px 1px rgba(0, 0, 0, 0.2)'}
              activeBoxShadow={'0 0 1px 1px rgba(0, 0, 0, 0.08)'}
              width={51}
              height={28}
              onChange={checked => changePowerConfig(checked)}
              checked={powerSwitchStatus}
            />
          </div>
          <div styleName="card-desc">
            开启后，设备在每天规定时间内自动开关机，延长设备续航时间。
          </div>
          {powerSwitchStatus ? (
            <div styleName="time-wrapper">
              <div
                className="flex"
                styleName="time-item"
                onClick={() => controlTimePicker({ onTime: true })}
              >
                <span styleName="time-text">开机</span>
                <span styleName="time-str">
                  {time.onTime}
                  <i className="iconfont icon-icon-action-arrow " />
                </span>
              </div>
              <div
                className="flex"
                styleName="time-item"
                onClick={() => controlTimePicker({ offTime: true })}
              >
                <span styleName="time-text">关机</span>
                <span styleName="time-str">
                  {time.offTime}
                  <i className="iconfont icon-icon-action-arrow " />
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {showTimePicker.onTime ? (
        <DateTimePicker
          title="开机时间"
          value={time.onTime}
          onConfirm={(value: string) => confirmTime(value, 'onTime')}
          onClose={() => controlTimePicker({ onTime: false })}
        />
      ) : null}
      {showTimePicker.offTime ? (
        <DateTimePicker
          title="关机时间"
          value={time.offTime}
          onConfirm={(value: string) => confirmTime(value, 'offTime')}
          onClose={() => controlTimePicker({ offTime: false })}
        />
      ) : null}
    </div>
  );
};

export default DevicePowerPlan;
